import { Capacitor } from '@capacitor/core'
import { Box, SxProps, Theme } from '@mui/material'
import { useWindowHeight } from '@react-hook/window-size'
import merge from 'lodash/merge'
import {
  ComponentProps,
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { isIOS } from 'react-device-detect'

import {
  ArtistBottomNavigation,
  GlobalBottomNavigation,
  ReplyForm,
} from '@/components/organisms'
import { ActionLinkCell as ThreadForm } from '@/components/pages/ThreadCreate/template/index'
import { styles, styleSets } from '@/components/styles'
import { useArtistThemeContext } from '@/components/styles/ArtistThemeProvider'
import { config } from '@/config'
import { useRouter } from '@/core'
import { CONSOLE_HEADER, useSafeAreaSetting } from '@/lib/safeAreaView'

import ConsoleFooter from './ConsoleFooter'
import { Header, HeaderProps } from './Header'

export const BaseLayout = forwardRef<HTMLDivElement, BaseLayoutProps>(
  (
    {
      children,
      header,
      margin,
      sx,
      responsive = false,
      showScrollBar = false,
      navigation,
      customNavigation,
      customNavigationSx,
      safeArea,
      black,
      hasConsoleFooter,
      maxWidth = 600,
    },
    ref
  ) => {
    const isNative = Capacitor.isNativePlatform()
    const isNativeIOS = isNative && isIOS
    const {
      router: { pathname },
    } = useRouter()

    useSafeAreaSetting(prevState =>
      merge(prevState, pathname.includes('console') ? CONSOLE_HEADER : {})
    )
    const [{ bgImage }] = useArtistThemeContext()
    const [bgStyle, setBgStyle] = useState<SxProps>({})
    const [bottomMargin, setBottomMarginHeight] = useState(0)

    const { router } = useRouter()
    const contentRef = useRef<HTMLDivElement>(null)
    const windowHeight = useWindowHeight()

    // 余白の大きさを決定する
    useEffect(() => {
      if (
        (!navigation && !customNavigation && !hasConsoleFooter) ||
        !contentRef.current
      ) {
        setBottomMarginHeight(0)
        return
      }
      const footerElm = contentRef.current.nextSibling as HTMLDivElement
      const footerHeight = footerElm?.offsetHeight ?? 0
      const scrollHeight = contentRef.current.scrollHeight ?? 0

      if (
        (!footerElm?.classList.contains('bottomNavigation') &&
          !footerElm?.classList.contains('customBottomNavigation') &&
          !footerElm?.classList.contains('consoleFooter')) ||
        (scrollHeight ?? 0) <= windowHeight - footerHeight
      ) {
        setBottomMarginHeight(0)
      } else {
        setBottomMarginHeight(footerElm?.offsetHeight ?? 0)
      }
    }, [navigation, customNavigation, contentRef.current])

    // アーティストの背景色、またはsvgを設定する
    useEffect(() => {
      setBgStyle({
        [isNativeIOS
          ? '.artist-theme-provider & .BgImage'
          : '.artist-theme-provider &']: {
          backgroundImage: bgImage ? `url(${bgImage})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          bgcolor: bgImage ? undefined : black ? '#222' : 'background.default',
          height: '100%',
        },
      })
    }, [bgImage, router.pathname, black])

    return (
      <Box sx={{ height: '100%' }}>
        <Box
          className={`BaseLayout ${
            pathname.includes('console') ? 'Console' : ''
          }`}
          ref={ref}
          sx={{
            position: 'relative',
            zIndex: 1,
            width: responsive ? undefined : '100%',
            pt: safeArea?.pt ?? styles.safeAreaTop,
            pb: safeArea?.pb ?? styles.safeAreaBottom,
            minHeight: `calc(100dvh - ${
              safeArea?.pt ?? styles.safeAreaTop
            }px - ${
              safeArea?.pb ?? styles.safeAreaBottom
            } - ${bottomMargin}px - ${header ? '56px' : '0px'})`,
            overflowX: isNativeIOS ? undefined : 'hidden',
            height: isNativeIOS ? '100%' : undefined,
            ...(styleSets.hideScrollbar() as {}),
            ...(bgStyle as {}),
            ...sx,
          }}
        >
          {header && (
            <Header
              {...{
                ...header,
                color: pathname.includes('console') ? 'white' : header.color,
                bgcolor: pathname.includes('console')
                  ? 'primary.main'
                  : header.color,
                position: 'fixed',
              }}
            />
          )}
          {/* 背景svgの後ろにテーマ背景色をフルスクリーンで設置する */}
          {!!bgImage && (
            <Box
              className="BgImage"
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: -1,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bgcolor: 'background.default',
                  width: `calc((100% - ${maxWidth}px) / 2)`,
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: -1,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bgcolor: 'background.default',
                  width: `calc((100% - ${maxWidth}px) / 2)`,
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: -1,
                }}
              />
            </Box>
          )}
          <Box
            className="BaseLayoutContent"
            ref={contentRef}
            sx={{
              ...(showScrollBar ? {} : styleSets.hideScrollbar()),
              maxWidth: responsive ? '100%' : maxWidth,
              margin: '0 auto',
              width: '100%',
              height: '100%',
              px: margin === true ? 5 : +(margin ?? 0),
              bgcolor: black ? '#222' : 'transparent',
              pt: header ? '56px' : 0,
              minHeight: bgImage
                ? `calc(100dvh - ${safeArea?.pt ?? styles.safeAreaTop} - ${
                    safeArea?.pb ?? styles.safeAreaBottom
                  } - ${bottomMargin}px - ${header ? '56px' : 0})`
                : 'auto',
              '&:focus': {
                border: 'none',
                outline: 'none',
              },
            }}
          >
            {children}
            {/* ボトムナビゲーションのための余白要素 */}
            <Box
              className="bottomMargin"
              sx={{
                height: bottomMargin ?? 0,
                width: '100%',
              }}
            />
          </Box>

          {!!navigation && (
            <Box
              className="bottomNavigation"
              sx={{
                position: 'fixed',
                insetX: 0,
                bottom: 0,
                right: 0,
                left: 0,
                margin: '0 auto',
                width: '100%',
                zIndex: 10,
                bgcolor: 'grey.50',
                pb: styles.safeAreaBottom,
                maxWidth: navigation.responsive ? '100%' : maxWidth,
              }}
            >
              {navigation.type === 'global' && (
                <GlobalBottomNavigation
                  tabKeys={config.globalTabs}
                  {...(navigation.props as Omit<
                    ComponentProps<typeof GlobalBottomNavigation>,
                    'tabKeys'
                  >)}
                />
              )}
              {navigation.type === 'artist' && (
                <ArtistBottomNavigation
                  tabKeys={config.artistTabs}
                  {...(navigation.props as Omit<
                    ComponentProps<typeof ArtistBottomNavigation>,
                    'tabKeys'
                  >)}
                />
              )}
              {navigation.type === 'replyForm' && (
                <ReplyForm
                  maxWidth={maxWidth}
                  {...(navigation.props as ComponentProps<typeof ReplyForm>)}
                />
              )}
              {navigation.type === 'threadForm' && (
                <ThreadForm
                  {...(navigation.props as ComponentProps<typeof ThreadForm>)}
                />
              )}
            </Box>
          )}
          {!!customNavigation && (
            <Box
              className="customBottomNavigation"
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                width: '100%',
                zIndex: 10,
                maxWidth: responsive ? '100%' : maxWidth,
                margin: '0 auto',
                bgcolor: 'grey.50',
                pb: styles.safeAreaBottom,
                ...customNavigationSx,
              }}
            >
              {customNavigation}
            </Box>
          )}
          {hasConsoleFooter && (
            <Box
              className="consoleFooter"
              sx={{
                position: 'fixed',
                insetX: 0,
                bottom: 0,
                right: 0,
                left: 0,
                margin: '0 auto',
                width: '100%',
                zIndex: 10,
                bgcolor: 'background.default',
                maxWidth: responsive ? '100%' : maxWidth,
              }}
            >
              <ConsoleFooter />
            </Box>
          )}
        </Box>
      </Box>
    )
  }
)

export type BaseLayoutProps = {
  hasConsoleFooter?: boolean
  header?: HeaderProps
  margin?: boolean | number
  px?: number
  children: ReactNode
  sx?: SxProps<Theme>
  // コンテンツの表示領域を画面横幅に合わせるかどうか
  responsive?: boolean
  // コンテンツの表示領域の最大横幅（デフォルトは600px）
  maxWidth?: number | string
  // スクロールを下から行う場合true
  scrollFromBottom?: boolean
  // スクロールバーを表示する
  showScrollBar?: boolean
  // ボトムナビゲーションのデータ
  navigation?: {
    type: 'global' | 'artist' | 'replyForm' | 'threadForm'
    responsive?: boolean
    props?:
      | Omit<ComponentProps<typeof GlobalBottomNavigation>, 'tabKeys'>
      | Omit<ComponentProps<typeof ArtistBottomNavigation>, 'tabKeys'>
      | ComponentProps<typeof ReplyForm>
      | ComponentProps<typeof ThreadForm>
  }
  safeArea?: {
    pt?: number | string
    pb?: number | string
  }
  // ボトムナビゲーションを自由に設定したい場合はこちら
  customNavigation?: ReactNode
  customNavigationSx?: SxProps
  // コンテンツ背景色を黒にするかどうか
  black?: boolean
  // 管理画面かどうか
  isConsole?: boolean
}
